import {
    combineLatest as observableCombineLatest,
    empty as observableEmpty,
    Observable,
    of as observableOf,
    ReplaySubject,
    Subscription
} from 'rxjs';

import {map, mergeMap, take, tap} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {BlobId, ProjectId, RoleId, UserId} from '../types/ids';
import {User} from '../types/user/user';
import {isDefined, isUUID} from '../utils/utils';
import {UserInfo} from '../types/user/user-info';
import {AuthService} from './auth.service';
import {UserApi} from '../api/rest/user.restapi';
import {AdminApi} from '../api/rest/administration.restapi';
import {BusinessTransaction} from '../types/business-transaction';
import {CustomerService} from '@webmate-web-core/shared/services/customer.service';
import {ApiKey} from '@webmate-web-core/shared/types/user/apikey';
import {GenericApiResponse} from '@webmate-web-core/shared/api/apibase.api';
import {PermissionService} from '@webmate-web-core/shared/services/permission.service';

/**
 * Responsible for everything related to webmate users.
 */
@Injectable({
    providedIn: 'root'
})
export class UserService {

    private _userProfileCache$: { [s: string /* UserId or 'current_user'*/]: ReplaySubject<any /* UserProfile or '{}' */> } = {};
    private _userProfileDirectCache: { [s: string /* UserId or 'current_user'*/]: any /* UserProfile or '{}' */ } = {};
    private _userInfoCache$: { [s: string /* UserId */]: ReplaySubject<UserInfo> } = {};
    private _userInfoDirectCache: { [s: string /* UserId */]: UserInfo } = {};

    private _userInfoByProject$: { [s: string /* ProjectId */]: ReplaySubject<UserId[]> } = {};
    private _userInfoByProjectCache: { [s: string /* ProjectId */]: UserId[] } = {}; // direct cache for project to user id
    private _currentUserId?: UserId = undefined; //once we know the ID of the 'current_user', we store the ID here and remap :-)
    private _currentUserCache?: User = undefined;
    private _allUsersForCurrentCustomer: ReplaySubject<User[]>;
    private _allUsersForCurrentCustomerCurrent: User[];

    private _permissionCache: { [s: string /* ProjectId */]: ReplaySubject<any> } = {};

    private reloadPermissionSubscription: Subscription;

    constructor(private oAuth2AuthService: AuthService, private userApi: UserApi, private customerService: CustomerService,
                private adminApi: AdminApi) {
        this._userProfileCache$['current_user'] = new ReplaySubject(1);
        this.oAuth2AuthService.observeAuthStatus().subscribe(result => {
            if (result && this._currentUserId === undefined) {
                // subscription is safe here, because of internal take(1)
                this.fetchCurrentUser().subscribe();
            }
        });
    }

    private fetchCurrentUser(): Observable<User> {
        return this.userApi.getCurrentUser().pipe(map(user => {
            this._currentUserId = user.id;
            this._currentUserCache = user;
            this._userProfileCache$['current_user'].next(this._currentUserCache);
            this._userProfileDirectCache['current_user'] = this._currentUserCache;
            if (this._userInfoCache$[user.id] === undefined) this._userInfoCache$[user.id] = new ReplaySubject(1);
            this._userInfoCache$[user.id].next(user as UserInfo);
            this._userInfoDirectCache[user.id] = (user as UserInfo);
            return user;
        }), take(1));
    }


    private fetchUserById(userId: UserId): Observable<User> {
        return this.userApi.getUser(userId).pipe(map(user => {
            this.addUserProfileToCaches(user);
            return user;
        }), mergeMap(afterwards => this._userProfileCache$[userId]));
    }

    private fetchAllUsersForCustomer() {
        return this.adminApi.getAllUserProfilesForCustomer().pipe(tap(users => {
            if (!this._allUsersForCurrentCustomer) {
                this._allUsersForCurrentCustomer = new ReplaySubject(1);
            }
            this._allUsersForCurrentCustomer.next(users);
            this._allUsersForCurrentCustomerCurrent = users;
            users.forEach(u => this.addUserProfileToCaches(u));
            return users;
        }), take(1));
    }

    private addUserProfileToCaches(user: User) {
        if (this._userInfoCache$[user.id] === undefined) this._userInfoCache$[user.id] = new ReplaySubject(1);
        this._userInfoCache$[user.id].next(user as UserInfo); //generate the UserInfo on the fly too, no need to query that ever again then :-)
        this._userInfoDirectCache[user.id] = (user as UserInfo);
        if (this._userProfileCache$[user.id] === undefined) this._userProfileCache$[user.id] = new ReplaySubject(1);
        this._userProfileCache$[user.id].next(user);
        this._userProfileDirectCache[user.id] = user;
    }

    refreshCaches() {
        return this.fetchCurrentUser().pipe(mergeMap(() => {
            return this.fetchAllUsersForCustomer();
        }));
    }

    /**
     * Requests the Profile of the CURRENTLY AUTHORIZED USER, returns the corresponding Observable! Note that the returned Observable will NOT PRODUCE A VALUE until
     * there is an authorized current User, making this compatible with components that exist both in an authorized and in a non-authorized setting.
     * THIS METHOD SHOULD BE USED IN EVERY QUERYING SERVICE CALL IN THE ENTIRE APPLICATION, TO AVOID QUERYING WHEN WE ARE NOT YET AUTHENTICATED!
     */
    getCurrentUserData(): Observable<User> {
        return this._userProfileCache$['current_user'].pipe(mergeMap(next => {
            if (isDefined(next) && isDefined(next.id) && isUUID(next.id) && isDefined(next.name)) {
                return observableOf(next);
            } else {
                return observableEmpty();
            }
        }));
    }

    /**
     * Requests (public) information about some User. *THIS IS THE METHOD TO USE* when encountering some UserId somewhere...
     */
    getInformationAboutUser(userId: UserId): Observable<UserInfo> {
        if (this._userInfoCache$[userId] !== undefined) {
            return this._userInfoCache$[userId].asObservable();
        } else {
            this._userInfoCache$[userId] = new ReplaySubject(1);
            return this.userApi.getUserInfo(userId).pipe(
                map(userInfo => {
                    this._userInfoCache$[userId].next(userInfo);
                    this._userInfoDirectCache[userId] = userInfo;
                }),
                mergeMap(afterwards => this._userInfoCache$[userId].asObservable()));
        }
    }

    /** returns the Ids of all Users of a Project (and makes certain the corresponding UserInfo is available).
     * *NOTE:* the user must be either a Member of the Project, or an Admin to obtain this Information!  */
    getUsersIdsOfProject(projectId: ProjectId): Observable<UserId[]> {
        if (this._userInfoByProject$[projectId] !== undefined) return this._userInfoByProject$[projectId].asObservable();
        this._userInfoByProject$[projectId] = new ReplaySubject(1);
        return this.userApi.getUserInfosForProject(projectId)
        .pipe(map(userInfos => {
            // fill the UserInfo-Cache
            let idsOnly = [];
            for (let userInfo of userInfos) {
                if (this._userInfoCache$[userInfo.id] === undefined) this._userInfoCache$[userInfo.id] = new ReplaySubject(1);
                this._userInfoCache$[userInfo.id].next(userInfo);
                this._userInfoDirectCache[userInfo.id] = userInfo;
                idsOnly.push(userInfo.id);
            }
            this._userInfoByProjectCache[projectId] = idsOnly;
            this._userInfoByProject$[projectId].next(idsOnly);
        }), mergeMap(afterwards => this._userInfoByProject$[projectId].asObservable()));
    }

    /** returns the Ids of all Users of a Project (and makes certain the corresponding UserInfo is available).
     * *NOTE:* the user must be either a Member of the Project, or an Admin to obtain this Information!  */
    getUsersOfProject(projectId: ProjectId): Observable<UserInfo[]> {
        let self = this;
        return this.getUsersIdsOfProject(projectId).pipe(mergeMap(userIds => {
            let tmp: Observable<UserInfo>[] = userIds.map(userId => self.getInformationAboutUser(userId!));
            let tmp2: Observable<UserInfo[]> = tmp.length == 0 ? observableOf([]) : observableCombineLatest(tmp);
            return tmp2;
        }));
    }

    /** requests a specific Profile from the Cache if possible or from the Backend instead! This is NOT intended for busy minor Information of a User, only
     * for busy your own data as well as several *ADMINISTRATIVE* Use-Cases! Note that if there is an error, the Observable will NEVER produce a value! */
    getUserProfileById(userId: UserId): Observable<User> {
        if (userId === this._currentUserId) userId = 'current_user'; //we already have it cached, but not under the UserId ;-)
        if (this._userProfileCache$[userId] !== undefined) return this._userProfileCache$[userId];
        this._userProfileCache$[userId] = new ReplaySubject(1);
        return this.getCurrentUserData().pipe(mergeMap(weAreAuthenticated => this.fetchUserById(userId)));
    }


    /** Requests the Projects of the CURRENTLY AUTHORIZED USER from Backend or from Cache. The returned Observable will produce an empty List until there is an authorized current User! */
    getUserProjects(): Observable<ProjectId[]> {
        return this.getCurrentUserData().pipe(map(profile => (profile['projects'] && profile['projects'].length >= 0) ? profile['projects'] : []));
    }

    /**
     * Requests all Profiles of Users that belong to the current Customer from the Cache or Backend. Returns the Ids.
     * BEWARE, this method internally requests UserProfiles, that means it is only meant for *ADMINISTRATIVE* UseCases (plus, it is not cheap!)
     */
    getAllUserIdsForCurrentCustomer(): Observable<UserId[]> {
        return this.getAllUsersForCurrentCustomer().pipe(map(users => users.map(user => user.id)));
    }
    /**
     * Requests all Profiles of Users that belong to the current Customer from the Cache or Backend. Returns the full UserProfiles.
     * BEWARE, this method internally requests UserProfiles, that means it is only meant for *ADMINISTRATIVE* UseCases (plus, it is not cheap!)
     */
    getAllUsersForCurrentCustomer(): Observable<User[]> {
        if (isDefined(this._allUsersForCurrentCustomer)) {
            return this._allUsersForCurrentCustomer.asObservable();
        }
        return this.fetchAllUsersForCustomer().pipe(mergeMap(() => this._allUsersForCurrentCustomer.asObservable()));
    }

    /** Requests the latest btx invoked by the user. This is an *ADMINISTRATIVE* UseCase */
    getLatestActionForUser(userId: UserId): Observable<BusinessTransaction> {
        return this.userApi.getLatestUserAction(userId);
    }

    /** Requests the deletion§ of a User that is not the currently authorized User, is an *ADMINISTRATIVE* UseCase */
    requestDeletionOfUser(userId: UserId): Observable<any> {
        if (this._currentUserId === userId) throw new Error('Cannot delete the currently authorized User!');

        return this.adminApi.deleteUser(userId).pipe(mergeMap(userWasDeleted => {
            return this.refreshCaches();
        }));
    }

    deleteUsersBulk(userIds: Array<UserId>) {
        return this.adminApi.deleteUsersBulk(userIds).pipe(mergeMap(() => this.refreshCaches()));
    }

    setUsersActiveBulk(userIds: Array<UserId>, active: boolean) {
        return this.adminApi.setUsersActiveBulk(userIds, active).pipe(mergeMap(() => this.refreshCaches()));
    }

    /** Tries to set a new AccountState for a User that is not the currently authorized User, is an *ADMINISTRATIVE* UseCase */
    requestUserAccountStateChange(userId: UserId, setToActive: boolean): Observable<any> {
        if (this._currentUserId === userId) throw new Error('Cannot delete the currently authorized User!');
        let tmp = setToActive ? this.adminApi.activateUserAccount(userId) :
            this.adminApi.deactivateUserAccount(userId);
        return tmp.pipe(mergeMap(userWasUpdated => {
            return this.userApi.getUser(userId).pipe(map(user => {
                if (this._userProfileCache$[userId] === undefined) {
                    /* this should be kind of impossible but one never knows, better safe than sorry */
                    this._userProfileCache$[userId] = new ReplaySubject(1);
                }
                this._userProfileCache$[userId].next(user); //update the cached value!
                this._userProfileDirectCache[userId] = user;
            }));
        }), mergeMap(() => this.refreshCaches()), take(1));
    }

    resendInvitationBulk(userIds: Array<UserId>) {
        return this.adminApi.resendInvitationBulk(userIds);
    }

    updateUserPropertiesBulk(userIds: Array<UserId>, properties: any) {
        return this.adminApi.updateUserPropertiesBulk(userIds, properties).pipe(mergeMap(() => this.refreshCaches()));
    }

    /** Attempts to update the properties of the user*/
    changeUserProperties(name: string, publicProperties: any, optBlobId?: BlobId, optNewEmail?: string): Observable<any> {
        return this.userApi.changeUserProperties(name, publicProperties, optBlobId, optNewEmail).pipe(mergeMap(() => {
            return this.fetchCurrentUser();
        }));
    }

    /** Requests PW-Change for the currently authorized User */
    changePassword(oldPassword: string, newPassword: string): Observable<any> {
        return this.userApi.changePassword(oldPassword, newPassword);
    }

    /** Requests a new API-Key for the currently authorized User. */
    requestNewApiKey(name: string): Observable<ApiKey> {
        return this.userApi.createNewApiKey(name);
    }

    getApiKeys(): Observable<ApiKey[]> {
        return this.userApi.getApiKeys();
    }

    deleteApiKey(id: string): Observable<GenericApiResponse> {
        return this.userApi.deleteApiKey(id);
    }

    setAdministrativeAccessForUsersBulk(userIds: Array<UserId>, allowAdminAccess: boolean) {
        return this.adminApi.setAdministrativeAccessForUsersBulk(userIds, allowAdminAccess).pipe(mergeMap(() => this.refreshCaches()));
    }

    setAdministrativeAccessForUser(userId: UserId, allowAdminAccess: boolean): Observable<any> {
        return this.adminApi.setAdministrativeAccess(userId, allowAdminAccess).pipe(mergeMap(
            /* re-query the Info from the Backend */
            afterwards => this.fetchUserById(userId)), mergeMap(() => this.refreshCaches()), take(1));
    }

    getPermissionsForCurrentUserInProject(projectId: ProjectId): Observable<string[]> {
        if (this._permissionCache[projectId] !== undefined) {
            return this._permissionCache[projectId].asObservable();
        }
        this._permissionCache[projectId] = new ReplaySubject(1);
        return this.adminApi.getPermissionsForCurrentUserInProject(projectId).pipe(map(perms => this._permissionCache[projectId].next(perms)), mergeMap(_ => this._permissionCache[projectId]));
    }

    hasPermissionInProject(projectId: ProjectId, permission: string): Observable<boolean> {
        return this.getPermissionsForCurrentUserInProject(projectId).pipe(map(permissions => permissions.indexOf(permission) > -1));
    }

    hasAdminPermission(permission: string): Observable<boolean> {
        return this.getCurrentUserData().pipe(map(user => user.administrativePermissions.find(p => p === permission) !== undefined));
    }

    hasOrgWidePermission(permission: string): Observable<boolean> {
        return this.customerService.getCustomerInfo$().pipe(mergeMap(info => {
            return this.getPermissionsForCurrentUserInProject(info.baseProject)
                .pipe(map(permissions => permissions.indexOf(permission) > -1));
        }));
    }

    updateCurrentUserCacheWithProjectId(projectId: ProjectId) {
        if (this._currentUserCache?.projects.indexOf(projectId) == -1) {
            this._currentUserCache.projects.push(projectId);
            this._userProfileCache$['current_user'].next(this._currentUserCache);
            this._userProfileDirectCache['current_user'] = this._currentUserCache;
        }
    }

    updateUserCacheWithProjectId(userId: UserId, projectId: ProjectId) {
        if (this._currentUserCache?.id === userId) {
            this.updateCurrentUserCacheWithProjectId(projectId);
        } else {
            if (isDefined(this._userInfoByProjectCache[projectId])) {
                this._userInfoByProjectCache[projectId].push(userId);
                this._userInfoByProject$[projectId].next(this._userInfoByProjectCache[projectId]);
            }
        }

        if (this._allUsersForCurrentCustomerCurrent !== undefined) {
            let index = this._allUsersForCurrentCustomerCurrent.findIndex(u => u.id === userId);
            if (index > -1) {
                this._allUsersForCurrentCustomerCurrent[index].projects.push(projectId);
            }
            this._allUsersForCurrentCustomer.next(this._allUsersForCurrentCustomerCurrent);
        }
    }

    removeProjectFromUserCache(userId: UserId, projectId: ProjectId) {
        if (this._currentUserCache?.id === userId) {
            if (this._currentUserCache.projects.indexOf(projectId) !== -1) {
                this._currentUserCache.projects.splice(this._currentUserCache.projects.indexOf(projectId), 1);
                this._userProfileCache$['current_user'].next(this._currentUserCache);
                this._userProfileDirectCache['current_user'] = this._currentUserCache;
            }
        } else {
            if (isDefined(this._userInfoByProjectCache[projectId])) {
                this._userInfoByProjectCache[projectId].splice(this._userInfoByProjectCache[projectId].indexOf(userId), 1);
                this._userInfoByProject$[projectId].next(this._userInfoByProjectCache[projectId]);
            }
        }
        let index = this._allUsersForCurrentCustomerCurrent.findIndex(u => u.id === userId);
        if (index > -1) {
            let pindex = this._allUsersForCurrentCustomerCurrent[index].projects.findIndex(p => p === projectId);
            if (pindex > -1) {
                this._allUsersForCurrentCustomerCurrent[index].projects.splice(pindex, 1);
            }
        }
        this._allUsersForCurrentCustomer.next(this._allUsersForCurrentCustomerCurrent);
    }

    reloadAllProjectPermissions() {
        for (let projectId in this._permissionCache) {
            this.reloadPermissionCacheForProject(projectId);
        }
    }

    reloadPermissionCacheForProject(projectId: ProjectId) {
        if (this._permissionCache[projectId] === undefined) this._permissionCache[projectId] = new ReplaySubject(1);
        if (isDefined(this.reloadPermissionSubscription)) this.reloadPermissionSubscription.unsubscribe();
        this.reloadPermissionSubscription = this.adminApi.getPermissionsForCurrentUserInProject(projectId).subscribe(perms => this._permissionCache[projectId].next(perms));
    }

    downloadUserInfoAsCSV(): Observable<Blob> {
        return this.adminApi.downloadUserInfoAsCSV();
    }

    updateUser(userId: UserId, name: string, email?: string, properties?: any): Observable<any> {
        return this.adminApi.updateUser(userId, name, email, properties).pipe(mergeMap(() => {
            return this.refreshCaches();
        }), take(1));
    }

}
